import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Target, Lightbulb, Sliders, AlertCircle, RefreshCw, TrendingDown, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { StatusBadge } from "@/components/StatusBadge";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Disclaimer } from "@/components/Disclaimer";
import { useApp } from "@/context/AppContext";
import { computeScenario, largestCoreSalary, metricsFrom, primitivesFromData } from "@/lib/calculations";
import { generateRecommendations } from "@/lib/recommendations";
import { formatCompact, formatMonths, runwayDisplay, runwayFull, formatPercent, formatDateID } from "@/lib/currency";
import { simulationAccessLabel } from "@/lib/storage";
import { subscriptionLabel } from "@/lib/subscription";
import { getPricing, formatPrice } from "@/lib/pricing";
import { RunwayCalc } from "@/components/RunwayCalc";
import { ResilienceScore } from "@/components/ResilienceScore";
import { ResilienceExplainer } from "@/components/ResilienceExplainer";
import { exportReportPdf } from "@/lib/exportReportPdf";
import { Download } from "lucide-react";

const DEBT_STEPS = [
  { pct: 5, mult: 1.05 },
  { pct: 10, mult: 1.1 },
  { pct: 15, mult: 1.15 },
  { pct: 20, mult: 1.2 },
];

const PRIORITY_STYLES = {
  URGENT: "bg-rose-50 text-rose-700 border-rose-200",
  IMPORTANT: "bg-amber-50 text-amber-700 border-amber-200",
  OPTIMIZATION: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const PRIORITY_LABEL = { URGENT: "MENDESAK", IMPORTANT: "PENTING", OPTIMIZATION: "OPTIMASI" };
function PriorityBadge({ priority }) {
  return (
    <span
      data-testid={`priority-${priority}`}
      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold tracking-wide ${PRIORITY_STYLES[priority] || PRIORITY_STYLES.OPTIMIZATION}`}
    >
      {PRIORITY_LABEL[priority] || priority}
    </span>
  );
}

function MetricPill({ label, value, sub }) {
  return (
    <div className="rounded-2xl bg-zinc-50 p-4">
      <p className="text-[11px] text-zinc-500">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold text-zinc-900">{value}</p>
      {sub && <p className="text-[11px] text-zinc-500">{sub}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow" data-testid={testId}>
      <div className="mb-4 flex items-center gap-2">
        {Icon && (
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100">
            <Icon className="h-4 w-4 text-zinc-700" />
          </div>
        )}
        <h3 className="font-heading text-lg font-bold text-zinc-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function DebtSimulation({ data }) {
  const [step, setStep] = useState(DEBT_STEPS[1]); // default +10%
  const before = computeScenario(data, {});
  const after = computeScenario(data, { debtMultiplier: step.mult });
  return (
    <Section title="Jika Beban Cicilan Naik" icon={AlertCircle} testId="sim-debt-increase">
      <p className="-mt-2 mb-3 text-xs leading-relaxed text-zinc-500">
        Kondisi keluarga jika total cicilan naik. Pilih perkiraan kenaikannya di bawah.
      </p>
      <div className="mb-4 flex gap-2">
        {DEBT_STEPS.map((s) => (
          <button
            key={s.pct}
            data-testid={`debt-step-${s.pct}`}
            onClick={() => setStep(s)}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
              step.pct === s.pct ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"
            }`}
          >
            +{s.pct}%
          </button>
        ))}
      </div>
      <BeforeAfter before={before} after={after} testId="debt-before-after" />
    </Section>
  );
}

// Scenario simulation layer (read-only over the FROZEN V2.1 engine).
// Reuses metricsFrom() with a reduced sideTotal — no change to the scoring model.
function SideIncomeStressTest({ data, base }) {
  const [lossPct, setLossPct] = useState(0);
  const p = useMemo(() => primitivesFromData(data), [data]);
  const lostAmount = Math.round((p.sideTotal * lossPct) / 100);
  const remaining = Math.max(0, p.sideTotal - lostAmount);
  const stressed = useMemo(() => metricsFrom({ ...p, sideTotal: remaining }), [p, remaining]);

  const beforeRunwayLabel = base.isPositive ? "Positif" : runwayFull(base.survivalRunway);
  const afterRunwayLabel = stressed.isPositive ? "Positif" : runwayFull(stressed.survivalRunway);
  const scoreDelta = stressed.score - base.score;

  return (
    <Section title="Stress Test Penghasilan Tambahan" icon={TrendingDown} testId="side-income-stress-test">
      <p className="-mt-2 mb-4 text-xs leading-relaxed text-zinc-500">
        Geser untuk melihat dampak jika penghasilan tambahan berkurang sebagian atau
        hilang total, sementara gaji utama tetap ada.
      </p>

      <div className="mb-4 flex gap-2">
        {[25, 50, 75, 100].map((pct) => (
          <button
            key={pct}
            data-testid={`side-preset-${pct}`}
            onClick={() => setLossPct(pct)}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
              lossPct === pct ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"
            }`}
          >
            -{pct}%
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-zinc-600">Penghasilan tambahan hilang</span>
        <span data-testid="side-loss-pct" className="font-mono text-sm font-semibold text-rose-600">
          {lossPct}%
        </span>
      </div>
      <Slider
        data-testid="side-loss-slider"
        className="mt-2"
        value={[lossPct]}
        onValueChange={(v) => setLossPct(v[0])}
        max={100}
        step={1}
      />

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-zinc-50 p-4">
          <p className="text-[11px] text-zinc-500">Sisa penghasilan tambahan</p>
          <p data-testid="side-remaining-amount" className="mt-1 font-mono text-lg font-bold text-zinc-900">
            {formatCompact(remaining)}
          </p>
          <p className="text-[11px] text-zinc-400">dari {formatCompact(p.sideTotal)}</p>
        </div>
        <div className="rounded-2xl bg-rose-50 p-4">
          <p className="text-[11px] text-rose-500">Kehilangan</p>
          <p data-testid="side-lost-amount" className="mt-1 font-mono text-lg font-bold text-rose-700">
            {lostAmount > 0 ? "-" : ""}{formatCompact(lostAmount)}
          </p>
          <p className="text-[11px] text-rose-400">per bulan</p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-zinc-900 p-5 text-white">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-zinc-400">Skor Ketahanan</p>
          <div className="flex items-center gap-2 font-mono text-lg font-bold">
            <span className="text-zinc-400">{base.score}</span>
            <ArrowRight className="h-4 w-4 text-rose-400" />
            <span data-testid="side-score-after">{stressed.score}</span>
            <span className={`text-xs ${scoreDelta < 0 ? "text-rose-400" : "text-emerald-400"}`}>
              ({scoreDelta >= 0 ? "+" : ""}{scoreDelta})
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
          <p className="text-[11px] text-zinc-400">Survival Runway</p>
          <div className="flex items-center gap-2 font-mono text-lg font-bold">
            <span className="text-zinc-400">{beforeRunwayLabel}</span>
            <ArrowRight className="h-4 w-4 text-rose-400" />
            <span data-testid="side-runway-after">{afterRunwayLabel}</span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
          <p className="text-[11px] text-zinc-400">Status</p>
          <div className="flex items-center gap-2">
            <StatusBadge status={base.status} />
            <ArrowRight className="h-4 w-4 text-rose-400" />
            <span data-testid="side-status-after">
              <StatusBadge status={stressed.status} />
            </span>
          </div>
        </div>

        {!stressed.isPositive && (
          <p className="mt-4 font-mono text-[10px] text-zinc-400">
            Dana Darurat {formatCompact(stressed.emergency)} ÷ Defisit{" "}
            {formatCompact(stressed.monthlyDeficit)}/bln = {runwayFull(stressed.survivalRunway)}
          </p>
        )}
      </div>
    </Section>
  );
}

function ImprovementSimulator({ data, report }) {
  const [emergencyAdd, setEmergencyAdd] = useState(0);
  const [livingReduction, setLivingReduction] = useState(0);
  const [debtReduction, setDebtReduction] = useState(0);

  const heroTarget = largestCoreSalary(data);
  const heroMods = heroTarget === "husband" ? { husbandSalary: 0 } : { wifeSalary: 0 };
  const lostLabelSim =
    heroTarget === "husband"
      ? data.husbandName
        ? `gaji ${data.husbandName}`
        : "Gaji Suami"
      : data.wifeName
        ? `gaji ${data.wifeName}`
        : "Gaji Istri";

  const improvedBase = computeScenario(data, { emergencyAdd, livingReduction, debtReduction });
  const improvedHero = computeScenario(data, {
    ...heroMods,
    emergencyAdd,
    livingReduction,
    debtReduction,
  });

  const beforeRunway = report.hero.after;
  const runwayBeforeLabel = beforeRunway.isPositive ? "Positif" : runwayFull(beforeRunway.survivalRunway);
  const runwayAfterLabel = improvedHero.isPositive ? "Positif" : runwayFull(improvedHero.survivalRunway);

  const sliderStyle = "mt-2";

  return (
    <Section title="Apa yang Membuat Kondisi Lebih Kuat?" icon={Sliders} testId="improvement-simulator">
      <p className="-mt-2 mb-4 text-xs leading-relaxed text-zinc-500">
        Geser untuk melihat langsung bagaimana perubahan keuangan memengaruhi daya tahan
        keluarga (Survival Runway) dan status ketahanan.
      </p>
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-600">Tambah dana darurat</span>
            <span className="font-mono text-sm font-semibold text-emerald-600">
              +{formatCompact(emergencyAdd)}
            </span>
          </div>
          <Slider
            data-testid="sim-emergency-slider"
            className={sliderStyle}
            value={[emergencyAdd]}
            onValueChange={(v) => setEmergencyAdd(v[0])}
            max={100000000}
            step={5000000}
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-600">Kurangi pengeluaran / bulan</span>
            <span className="font-mono text-sm font-semibold text-emerald-600">
              -{formatCompact(livingReduction)}
            </span>
          </div>
          <Slider
            data-testid="sim-living-slider"
            className={sliderStyle}
            value={[livingReduction]}
            onValueChange={(v) => setLivingReduction(v[0])}
            max={Math.max(1000000, report.base.living)}
            step={500000}
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-600">Kurangi cicilan / bulan</span>
            <span className="font-mono text-sm font-semibold text-emerald-600">
              -{formatCompact(debtReduction)}
            </span>
          </div>
          <Slider
            data-testid="sim-debt-slider"
            className={sliderStyle}
            value={[debtReduction]}
            onValueChange={(v) => setDebtReduction(v[0])}
            max={Math.max(1000000, report.base.totalDebt)}
            step={500000}
          />
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-zinc-900 p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] text-zinc-400">Survival Runway</p>
            <div className="mt-1 flex items-center gap-2 font-mono text-lg font-bold">
              <span className="text-zinc-400">{runwayBeforeLabel}</span>
              <ArrowRight className="h-4 w-4 text-emerald-400" />
              <span data-testid="sim-runway-after">{runwayAfterLabel}</span>
            </div>
            {!improvedHero.isPositive && (
              <p className="mt-2 font-mono text-[10px] text-zinc-400">
                Dana Darurat {formatCompact(improvedHero.emergency)} ÷ Defisit{" "}
                {formatCompact(improvedHero.monthlyDeficit)}/bln = {runwayFull(improvedHero.survivalRunway)}
              </p>
            )}
            {improvedHero.isPositive && (
              <p className="mt-2 font-mono text-[10px] text-zinc-400">
                Cashflow tetap positif — sisa {formatCompact(improvedHero.monthlyBalance)}/bln meski {lostLabelSim} berhenti.
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
          <p className="text-[11px] text-zinc-400">Status</p>
          <div className="flex items-center gap-2">
            <StatusBadge status={report.base.status} />
            <ArrowRight className="h-4 w-4 text-emerald-400" />
            <StatusBadge status={improvedBase.status} />
          </div>
        </div>
      </div>
    </Section>
  );
}

function LockedCard({ title }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-zinc-100 bg-zinc-50 p-4">
      <Lock className="mb-2 h-4 w-4 text-zinc-400" />
      <p className="text-xs font-semibold leading-tight text-zinc-600">{title}</p>
    </div>
  );
}

export default function Resilience() {
  const navigate = useNavigate();
  const app = useApp();
  const report = app.report;

  if (!report) {
    return (
      <div className="px-6 pb-4 pt-8" data-testid="resilience-empty">
        <h1 className="mb-1 font-heading text-2xl font-extrabold text-zinc-900">Ketahanan Keuangan Anda</h1>
        <p className="mb-6 text-sm text-zinc-400">Family Financial Stress Test</p>
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 text-center shield-shadow">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100">
            <ShieldCheck className="h-6 w-6 text-zinc-400" />
          </div>
          <p className="mt-3 font-heading text-base font-bold text-zinc-900">Belum ada pemeriksaan ketahanan</p>
          <p className="mx-auto mt-1 max-w-xs text-sm text-zinc-500">
            Cek seberapa kuat keuangan keluarga menghadapi kehilangan penghasilan, cicilan, dan pengeluaran tak terduga (±2 menit).
          </p>
          <button
            data-testid="resilience-start-cta"
            onClick={() => navigate("/assessment")}
            className="mt-5 inline-flex h-12 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm font-semibold text-white"
          >
            Cek Ketahanan Sekarang <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const { premium, simulationActive, subscriptionUntil } = app;
  const pricing = getPricing();
  const { base, hero, husbandLoss, wifeLoss, target, plan } = report;
  const data = report.data;

  const readOnly = premium && !simulationActive;
  const heroDisp = runwayDisplay(hero.after.survivalRunway);
  const recs = generateRecommendations(base, data);
  const lostLabel =
    hero.target === "husband"
      ? data.husbandName
        ? `gaji ${data.husbandName}`
        : "Gaji Suami"
      : data.wifeName
        ? `gaji ${data.wifeName}`
        : "Gaji Istri";

  return (
    <div className="px-6 pb-4 pt-8">
      <h1 className="mb-1 font-heading text-2xl font-extrabold text-zinc-900">Ketahanan Keluarga</h1>
      <p className="mb-4 text-sm text-zinc-400">Family Financial Stress Test</p>

      <div className="mb-5 flex items-center justify-between gap-3 rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="min-w-0">
          <p className="text-xs text-zinc-500">Pemeriksaan terakhir</p>
          <p className="font-heading text-sm font-bold text-zinc-800" data-testid="resilience-last-date">
            {report.createdAt ? formatDateID(report.createdAt) : "—"}
          </p>
        </div>
        <button
          data-testid="resilience-retest-cta"
          onClick={() => navigate("/assessment")}
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-zinc-300 px-4 text-xs font-semibold text-zinc-700"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Tes Ulang Ketahanan
        </button>
      </div>

      {/* Desktop: 2 kolom (skor & ringkasan kiri, detail kanan); mobile: urutan menumpuk tak berubah */}
      <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        <div className="lg:col-span-5">
      {/* Always-visible core metrics */}
      <div className="rounded-3xl bg-zinc-900 p-6 text-white shield-shadow">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 font-heading">
          Family Survival Runway
        </p>
        {hero.after.isPositive ? (
          <p className="mt-2 font-heading text-2xl font-extrabold">Cashflow tetap positif</p>
        ) : (
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-mono text-5xl font-black">{heroDisp.value}</span>
            <span className="font-heading text-lg text-zinc-300">{heroDisp.unit}</span>
          </div>
        )}
        <div className="mt-4">
          <RunwayCalc scenario={hero.after} lostLabel={lostLabel} dark />
        </div>
        <ResilienceScore metrics={base} variant="hero" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <MetricPill label="Dana Darurat" value={`${formatMonths(base.emergencyMonths)} bln`} sub="Target 6 bulan" />
        <MetricPill label="Cicilan / Gaji Utama" value={`${formatPercent(base.debtToCoreIncome)}%`} sub={debtStatus(base.debtToCoreIncome)} />
        <MetricPill label="Ketergantungan Side" value={`${formatPercent(base.sideDependency)}%`} sub={depStatus(base.sideDependency)} />
        <MetricPill
          label="Surplus Bulanan"
          value={`${base.monthlyBalance >= 0 ? "" : "-"}${formatCompact(Math.abs(base.monthlyBalance))}`}
        />
      </div>

      {/* Plain-language explanation of the score (Correction #1) */}
      <ResilienceExplainer metrics={base} hero={hero} />

        </div>
        <div className="lg:col-span-7">
      {/* Read-only expired banner */}
      {readOnly && (
        <div
          className="mt-5 rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow lg:mt-0 lg:mb-5"
          data-testid="expired-report-banner"
        >
          <h3 className="font-heading text-lg font-bold text-zinc-900">Laporan Anda tetap tersedia</h3>
          <p className="mt-2 text-sm leading-relaxed text-zinc-500">
            Periode simulasi untuk laporan ini telah berakhir. Anda tetap dapat membaca
            hasil laporan kapan saja.
          </p>
          <Button
            data-testid="new-report-cta"
            onClick={() => navigate("/app/profil")}
            className="mt-4 h-12 w-full rounded-full bg-zinc-900 font-semibold hover:bg-zinc-800"
          >
            Update Kondisi &amp; Buat Laporan Baru — {formatPrice(pricing.price)}
          </Button>
        </div>
      )}

      {/* Premium content */}
      {premium ? (
        <div className="mt-5 space-y-4 lg:mt-0">
          <Button
            data-testid="download-pdf-btn"
            onClick={() => exportReportPdf(report, recs.recommendations || [])}
            variant="outline"
            className="h-11 w-full rounded-full border-zinc-300 text-sm font-semibold"
          >
            <Download className="mr-2 h-4 w-4" /> Unduh Laporan (PDF)
          </Button>
          {simulationActive && (
            <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5">
              <span className="text-xs font-medium text-emerald-700" data-testid="simulation-access-label">
                {subscriptionLabel(subscriptionUntil, Date.now(), formatDateID)}
              </span>
            </div>
          )}

          {/* Husband salary loss */}
          <Section title="Jika Gaji Suami Hilang" testId="sim-husband-loss">
            <p className="-mt-2 mb-3 text-xs leading-relaxed text-zinc-500">
              Kondisi keluarga jika gaji suami berhenti total, sementara gaji istri dan
              penghasilan tambahan tetap ada.
            </p>
            <BeforeAfter before={base} after={husbandLoss} testId="husband-before-after" />
          </Section>

          {/* Wife salary loss */}
          <Section title="Jika Gaji Istri Hilang" testId="sim-wife-loss">
            <p className="-mt-2 mb-3 text-xs leading-relaxed text-zinc-500">
              Kondisi keluarga jika gaji istri berhenti total, sementara gaji suami dan
              penghasilan tambahan tetap ada.
            </p>
            <BeforeAfter before={base} after={wifeLoss} testId="wife-before-after" />
          </Section>

          {/* Side income stress test — interactive slider (scenario layer) */}
          {base.sideTotal > 0 &&
            (simulationActive ? (
              <SideIncomeStressTest data={data} base={base} />
            ) : (
              <Section title="Stress Test Penghasilan Tambahan" icon={TrendingDown} testId="side-income-stress-static">
                <p className="-mt-2 mb-3 text-xs leading-relaxed text-zinc-500">
                  Kondisi keluarga jika penghasilan tambahan hilang total, sementara gaji
                  utama tetap ada.
                </p>
                <BeforeAfter before={base} after={report.sideStress} testId="side-before-after-static" />
                <p className="mt-3 text-xs text-zinc-400">
                  Slider stress test interaktif terkunci setelah periode akses berakhir.
                </p>
              </Section>
            ))}

          {/* Debt increase — interactive only when active */}
          {simulationActive ? (
            <DebtSimulation data={data} />
          ) : (
            <Section title="Jika Beban Cicilan Naik" icon={AlertCircle}>
              <BeforeAfter before={base} after={report.debtIncrease} testId="debt-before-after-static" />
              <p className="mt-3 text-xs text-zinc-400">Simulasi terkunci setelah periode akses berakhir.</p>
            </Section>
          )}

          {/* Emergency fund target */}
          <Section title="Target Dana Darurat" icon={Target} testId="emergency-target">
            <p className="-mt-2 mb-4 text-xs leading-relaxed text-zinc-500">
              Dana darurat idealnya cukup untuk menutup 6 bulan pengeluaran wajib
              (cicilan + kebutuhan rutin), sebagai penyangga bila pendapatan terganggu.
            </p>
            <div className="mb-4 rounded-xl bg-zinc-50 px-3 py-2 font-mono text-[11px] text-zinc-600" data-testid="emergency-target-calc">
              Pengeluaran wajib {formatCompact(base.essentialOutflow)} × 6 bulan ={" "}
              <b>{formatCompact(target.target)}</b>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-[11px] text-zinc-500">Target</p>
                <p className="font-mono text-sm font-bold text-zinc-900">{formatCompact(target.target)}</p>
              </div>
              <div>
                <p className="text-[11px] text-zinc-500">Saat ini</p>
                <p className="font-mono text-sm font-bold text-zinc-900">{formatCompact(target.current)}</p>
              </div>
              <div>
                <p className="text-[11px] text-zinc-500">Kekurangan</p>
                <p className="font-mono text-sm font-bold text-rose-600">{formatCompact(target.gap)}</p>
              </div>
            </div>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-zinc-100">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${Math.min(100, (target.months / 6) * 100)}%` }}
              />
            </div>
            <p className="mt-2 text-center text-xs text-zinc-500">
              {formatMonths(target.months)} dari target 6 bulan
            </p>
          </Section>

          {/* Action plan — Recommendation Engine V1 */}
          <Section title="Apa yang Bisa Diperkuat?" icon={Lightbulb} testId="action-plan">
            {recs.maintenance ? (
              <p className="text-sm text-zinc-500" data-testid="rec-maintenance">
                Ketahanan keluarga sudah tergolong kuat di area utama. Pertahankan kebiasaan
                finansial yang baik ini.
              </p>
            ) : (
              <div className="space-y-3">
                {recs.recommendations.map((r, i) => (
                  <div
                    key={r.id}
                    className="rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4"
                    data-testid={`rec-card-${i + 1}`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold text-zinc-400 font-heading">
                        Prioritas #{i + 1}
                      </span>
                      <PriorityBadge priority={r.priority} />
                    </div>
                    <p className="font-heading text-sm font-bold text-zinc-900">{r.title}</p>
                    <p className="mt-1 text-xs text-zinc-500">{r.current}</p>
                    <p className="mt-2 text-xs font-medium text-zinc-700">
                      <span className="text-zinc-400">Target: </span>{r.target}
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-zinc-600">{r.why}</p>
                    <p className="mt-2 text-xs font-medium text-emerald-700">→ {r.action}</p>
                    {!r.info && (
                      <div className="mt-3 flex items-center gap-2 rounded-xl bg-white px-3 py-2">
                        <span className="text-[11px] text-zinc-400">Estimasi skor</span>
                        <span className="font-mono text-xs font-bold text-zinc-900">
                          {r.scoreFrom} → {r.scoreTo}
                        </span>
                        <span className="font-mono text-[11px] font-semibold text-emerald-600">
                          ({r.delta >= 0 ? "+" : ""}{r.delta})
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                {!recs.edge && (
                  <p className="pt-1 text-[11px] text-zinc-400">
                    Estimasi skor adalah perkiraan bila target tercapai dan faktor lain tetap sama.
                  </p>
                )}
              </div>
            )}
          </Section>

          {/* Improvement simulator — interactive only when active */}
          {simulationActive ? (
            <ImprovementSimulator data={data} report={report} />
          ) : (
            <Section title="Apa yang Membuat Kondisi Lebih Kuat?" icon={Sliders}>
              <p className="text-sm text-zinc-500">
                Improvement Simulator terkunci setelah periode akses berakhir. Buat laporan baru
                untuk kembali mengaktifkan simulasi.
              </p>
            </Section>
          )}

          <Disclaimer className="pt-2" />
        </div>
      ) : (
        /* Free users — locked premium scenarios */
        <div className="mt-5 space-y-4 lg:mt-0">
          <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow">
            <h3 className="font-heading text-base font-bold leading-snug text-zinc-900">
              Buka stress test lengkap untuk melihat ketahanan keluarga secara menyeluruh.
            </h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                "Jika Gaji Suami Hilang",
                "Jika Gaji Istri Hilang",
                "Jika Beban Cicilan Naik",
                "Target Dana Darurat Ideal",
                "Batas Pengeluaran Aman",
                "Langkah Menuju Status Hijau",
              ].map((t) => (
                <LockedCard key={t} title={t} />
              ))}
            </div>
            <Button
              data-testid="resilience-open-paywall"
              onClick={() => navigate("/paywall")}
              className="mt-5 h-12 w-full rounded-full bg-zinc-900 font-semibold hover:bg-zinc-800"
            >
              Buka Laporan Lengkap — {formatPrice(pricing.price)}
            </Button>
          </div>
          <Disclaimer className="pt-2" />
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

function debtStatus(v) {
  if (v <= 30) return "Sehat";
  if (v <= 40) return "Perlu perhatian";
  return "Berat";
}
function depStatus(v) {
  if (v < 15) return "Rendah";
  if (v <= 30) return "Sedang";
  return "Tinggi";
}
